<template>
    <Modal :isOpen="isOpen" @close="close">
        <template #title>
            <div class="flex items-center gap-3">
                <div
                    class="w-12 h-12 p-3.5 bg-red-50 rounded-full flex items-center justify-center shrink-0"
                >
                    <Icon
                        icon="error"
                        class="w-6 h-6 text-red-500"
                        pathClass=""
                    />
                </div>
                <div>
                    <h2 class="text-xl font-medium text-slate-800 dark:text-slate-100">
                        Action Required to Resolve Your Connection
                    </h2>
                </div>
            </div>
        </template>

        <template #content>
            <div class="relative min-h-[120px]">
                <LoadingState
                    :loading="showLoading || submittingAnswer"
                    size="large"
                />
                <div v-if="!showLoading && !submittingAnswer">
                    <p class="text-sm text-slate-800 dark:text-slate-100 mb-4">
                        We need to resolve the challenge questions for your
                        {{ member.name }} account. Please answer the below
                        challenge question in order to connect your institution.
                    </p>
                    <form id="challenge-form">
                        <div
                            v-for="challenge in challenges"
                            class="mb-2 pt-3 pb-3"
                            :key="challenge.guid"
                        >
                            <label>
                                <span
                                    class="block w-full mb-2 text-sm font-medium text-slate-600"
                                >
                                    {{ challenge.label }}
                                </span>
                                <input
                                    v-if="challenge.type === 'TEXT'"
                                    type="text"
                                    class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 py-1.5 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800"
                                    :name="challenge.guid"
                                    v-model="challenge.answer"
                                    required
                                />
                                <select
                                    v-if="challenge.type === 'OPTIONS'"
                                    :name="challenge.guid"
                                    class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 py-1.5 text-gray-900 dark:text-gray-100 transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800"
                                    v-model="challenge.answer"
                                    required
                                >
                                    <option value=""></option>
                                    <option
                                        v-for="option in challenge.options"
                                        :value="option.value"
                                        :key="option.value"
                                    >
                                        {{ option.label }}
                                    </option>
                                </select>

                                <img
                                    v-if="challenge.type === 'IMAGE_DATA'"
                                    class="image-option mb-4"
                                    :src="challenge.image_data"
                                    :alt="challenge.label"
                                />
                                <input
                                    v-if="challenge.type === 'IMAGE_DATA'"
                                    type="text"
                                    class="block w-full bg-white dark:bg-slate-800 rounded border border-slate-200 py-1.5 text-gray-900 dark:text-gray-100 placeholder:text-slate-500 placeholder:text-sm transition-colors focus:ring-0 focus:border-slate-800 hover:border-slate-800"
                                    :name="challenge.guid"
                                    placeholder="Answer here ..."
                                    v-model="challenge.answer"
                                    required
                                />
                            </label>
                            <div v-if="challenge.type === 'IMAGE_OPTIONS'">
                                <label
                                    class="relative inline-block m-3 cursor-pointer hover:opacity-60"
                                    v-for="(
                                        option, i
                                    ) in challenge.image_options"
                                    :key="'image-option-' + i"
                                >
                                    <input
                                        type="radio"
                                        :name="challenge.guid"
                                        :value="option.value"
                                        class="image-option-value hidden"
                                        v-model="challenge.answer"
                                    />
                                    <span>
                                        <i class="fal fa-check-circle"></i>
                                    </span>
                                    <img
                                        :src="option.data_uri"
                                        class="image-option"
                                        :alt="challenge.label"
                                    />
                                </label>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </template>

        <template #footer>
            <div class="flex justify-end gap-2">
                <button
                    class="px-4 py-2.5 bg-white dark:bg-slate-800 rounded shadow dark:shadow-slate-900/50 text-sm font-medium border border-slate-200 text-slate-800 dark:text-slate-100 hover:bg-slate-50 transition-colors active:bg-slate-200 focus:outline-slate-200 focus:border-slate-200 hover:border-slate-300 active:border-slate-300"
                    @click="close"
                >
                    Cancel
                </button>
                <button
                    type="button"
                    @click="submitAnswer"
                    class="px-4 py-2.5 bg-slate-800 rounded shadow text-sm font-medium text-white hover:bg-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    :disabled="submittingAnswer || showLoading"
                    id="hs-wallet-challenges-submit"
                >
                    Submit Answer
                </button>
            </div>
        </template>
    </Modal>
</template>

<script>
import Modal from '@/components/ui/modal/ui-modal.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoadingState from '@/components/ui/loading-state/loading-state.vue';
import { handleError } from '@/utils/error-handler';

export default {
    name: 'ChallengesModal',
    props: {
        member: {
            type: Object,
        },
        isOpen: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['close', 'success'],
    components: {
        Modal,
        Icon,
        LoadingState,
    },
    watch: {
        isOpen(newVal) {
            if (newVal) {
                this.resetState();
                this.getChallenges();
            }
        },
    },
    data: () => ({
        showLoading: false,
        submittingAnswer: false,
        challenges: [],
        challengeAttempts: 0,
    }),
    methods: {
        resetState() {
            this.challenges = [];
            this.showLoading = false;
            this.submittingAnswer = false;
            this.challengeAttempts = 0;
        },
        getChallenges() {
            this.showLoading = true;

            ApiService.apiCall(
                '/members/' + this.member.guid + '/challenges',
                'get'
            )
                .then((response) => {
                    this.challenges = response.data.challenges;
                    this.showLoading = false;
                })
                .catch((response) => {
                    this.showLoading = false;
                    handleError(response);
                });
        },
        submitAnswer() {
            this.submittingAnswer = true;
            let answered = false;
            let answers = [];

            this.challenges.forEach((el) => {
                answers.push({ guid: el.guid, answer: el.answer });
                if (el.answer && el.answer.length > 0) {
                    answered = true;
                }
            });

            if (answered) {
                let data = {
                    challenge_answers: answers,
                };

                ApiService.apiCall(
                    '/members/' + this.member.guid + '/resume',
                    'put',
                    data
                )
                    .then((response) => {
                        if (response.data.success) {
                            this.$notify(
                                {
                                    group: 'success',
                                    title: 'Success',
                                    text: 'Your connection has been resolved successfully.',
                                },
                                4000
                            );
                            this.$emit('success');
                            this.close();
                        } else if (
                            response.data.status === 'CHALLENGED' &&
                            this.challengeAttempts < 5
                        ) {
                            this.challengeAttempts++;
                            this.submittingAnswer = false;
                            this.challenges = [];
                            this.getChallenges();
                        } else {
                            this.$notify(
                                {
                                    group: 'error',
                                    title: 'Error',
                                    text:
                                        'The request was ' +
                                        response.data.status +
                                        '. Please try again or contact support.',
                                },
                                4000
                            );
                            this.submittingAnswer = false;
                        }
                    })
                    .catch((response) => {
                        handleError(response);
                        this.submittingAnswer = false;
                    });
            } else {
                this.$notify(
                    {
                        group: 'error',
                        title: 'Error',
                        text: 'Please enter your challenge answer.',
                    },
                    4000
                );
                this.submittingAnswer = false;
            }
        },
        close() {
            this.resetState();
            this.$emit('close');
        },
    },
};
</script>

<style scoped>
.image-option {
    max-width: 140px;
    height: auto;
}
.image-option-value + span {
    display: none;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    font-size: 42px;
    border-radius: 50%;
    background-color: #fff;
    line-height: 1;
    text-align: center;
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 2;
}
.image-option-value:checked + span {
    display: flex;
}
</style>
